import React, { useState } from 'react'
import { useMutation } from '@apollo/client'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import {
  ClickAwayListener,
  Fade,
  Popper,
  Radio,
  Typography,
} from '@mui/material'

import Box from 'components/Common/Box'
import Button from 'components/Common/Button'
import Tag from 'components/Common/Tag'

import { StatusStyles } from 'constants/status'
import { textFiles } from 'constants/textFiles'
import useTranslation from 'hooks/useTranslation'
import { GenericData, GenericInputVariable } from 'models/services/base'
import { TemplateStatus } from 'models/status'

import { PUBLISH_TEMPLATE } from 'graphQL/Template/Detail/mutations'

import { colors, FONT_WEIGHT } from 'styles/theme'

import { StyledDisclaimerText, StyledStatusContainer } from './style'

type StatusChangePopperProps = {
  status: TemplateStatus
  setStatus: (status: TemplateStatus) => void
  templateId: string
}

const StatusChangePopper = ({
  status,
  setStatus,
  templateId,
}: StatusChangePopperProps) => {
  const { text } = useTranslation(textFiles.TEMPLATE_DETAIL)

  // Manage status popper
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [open, setOpen] = useState<boolean>(false)

  const handleMouseOver = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handlePopperOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    setOpen((prevState) => !prevState)
  }

  const [radioSelect, setRadioSelect] = useState<TemplateStatus>(status)

  const [publishTemplate] = useMutation<
    GenericData<string>,
    GenericInputVariable<string>
  >(PUBLISH_TEMPLATE)

  const handleRadioChange = () => {
    setRadioSelect(
      radioSelect === TemplateStatus.DRAFT
        ? TemplateStatus.PUBLISHED
        : TemplateStatus.DRAFT
    )
  }

  const handleSave = () => {
    setStatus(
      status === TemplateStatus.DRAFT
        ? TemplateStatus.PUBLISHED
        : TemplateStatus.DRAFT
    )
    setOpen(false)

    publishTemplate({
      variables: {
        input: templateId,
      },
    })
  }

  const handleCancel = () => {
    setOpen(false)
  }

  return (
    <>
      <Box
        sx={{
          width: '42px',
          height: '37px',
          color: StatusStyles[status].color,
          backgroundColor: StatusStyles[status].backgroundColor,
          border: `2px solid ${colors.commonWhite}`,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          '&:hover': {
            cursor: 'pointer',
          },
        }}
        onMouseEnter={handleMouseOver}
        onClick={handlePopperOpen}
      >
        <EditIcon />
      </Box>
      <Popper
        id="status-popover"
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        transition
        style={{
          zIndex: 1500,
        }}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener
            onClickAway={() => {
              setOpen(false)
              setRadioSelect(status)
            }}
          >
            <Fade {...TransitionProps} timeout={350}>
              <Box
                sx={{
                  backgroundColor: colors.commonWhite,
                  width: '500px',
                  height: '280px',
                  padding: '1.75em',
                  borderRadius: '4px',
                  boxShadow: `0px 3px 6px ${colors.shadow}`,
                }}
              >
                <Typography variant="body1" color={colors.blue}>
                  {text.modals.statusTitle}
                </Typography>

                <Box
                  sx={{
                    margin: '0.5em 0',
                    fontSize: '14px',
                    fontWeight: FONT_WEIGHT.REGULAR,
                    color: colors.black,
                  }}
                >
                  {text.modals.statusDescriptionFirstPart}{' '}
                  <b>{text.modals.statusDescriptionSecondPart}</b>{' '}
                  {text.modals.statusDescriptionThirdPart}{' '}
                  <b>{text.modals.statusDescriptionFourthPart}</b>{' '}
                  {text.modals.statusDescriptionFifthPart},{' '}
                  <b>{text.modals.statusDescriptionSixthPart}</b>
                </Box>
                <StyledStatusContainer>
                  <Radio
                    onChange={handleRadioChange}
                    checked={radioSelect === TemplateStatus.PUBLISHED}
                    sx={{
                      color: colors.blue,
                    }}
                  />
                  <Tag status={TemplateStatus.PUBLISHED} />
                  <StyledDisclaimerText>
                    {text.modals.publishedDisclaimer}
                  </StyledDisclaimerText>
                </StyledStatusContainer>
                <StyledStatusContainer>
                  <Radio
                    onChange={handleRadioChange}
                    checked={radioSelect === TemplateStatus.DRAFT}
                    sx={{
                      color: colors.blue,
                    }}
                  />
                  <Tag status={TemplateStatus.DRAFT} />
                  <StyledDisclaimerText>
                    {text.modals.draftDisclaimer}
                  </StyledDisclaimerText>
                </StyledStatusContainer>
                <Box
                  sx={{
                    margin: '2em 0 0.5em 0',
                    ' > button': {
                      height: '35px !important',
                      minWidth: '80px',
                    },
                  }}
                >
                  <Button
                    type="button"
                    buttonType="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    sx={{ marginRight: '1em' }}
                  >
                    {text.buttons.save}
                  </Button>
                  <Button
                    type="button"
                    buttonType="secondary"
                    onClick={handleCancel}
                  >
                    {text.buttons.cancel}
                  </Button>
                </Box>
              </Box>
            </Fade>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  )
}

export default StatusChangePopper
